import { TPdfLayoutType } from "@typed/api/responses/Organisation"

/**
 * Types mirroring the iOS PDF feature models.
 * iOS sources of truth:
 * - Sources/Entities/Model/PDF/WorkPDFPageType.swift
 * - Sources/Entities/Model/PDF/WorkPDFPageDataModel.swift
 * - Sources/Entities/Model/PDF/WorkPDFOptionsModel.swift
 * - Sources/Entities/Model/PDF/WorkPDFRowModel.swift
 */

export type TUnitSystem = "metric" | "imperial"

// iOS: WorkPDFImageUrlModel
export interface IPdfImageUrl {
	url: string
	backupUrl: string
}

// iOS: WorkPDFPageDataModel
export interface IPdfPageData {
	id: string
	artist: string
	title: string
	year: string
	medium: string
	price: string // thousands-formatted amount ("" when 0/absent)
	currencySymbol: string
	currencyCode: string
	width: string
	height: string
	depth: string
	otherDim: string
	note: string
	provenance: string
	exhibition: string
	literature: string
	edition: string
	unit: TUnitSystem
	images: IPdfImageUrl[]
}

// iOS: OrgAttachment
export interface IOrgAttachment {
	id: string
	artist: string
	pdfName: string
	pdfPath: string
	pdfImagePath: string
}

// iOS: PDFOptions / WorkPDFOptionsModel
export type TPdfOptionKey = "branded" | "price" | "notes" | "provenance" | "exhibition" | "literature"

export interface IPdfOptions {
	activeOptions: TPdfOptionKey[]
	hasBrand: boolean
}

export const defaultPdfOptions: IPdfOptions = {
	activeOptions: ["branded"],
	hasBrand: true,
}

export function showBrand(o: IPdfOptions): boolean {
	return o.activeOptions.includes("branded") && o.hasBrand
}
export function showPrice(o: IPdfOptions): boolean {
	return o.activeOptions.includes("price")
}
export function showNote(o: IPdfOptions): boolean {
	return o.activeOptions.includes("notes")
}
export function showProvenance(o: IPdfOptions): boolean {
	return o.activeOptions.includes("provenance")
}
export function showExhibitions(o: IPdfOptions): boolean {
	return o.activeOptions.includes("exhibition")
}
export function showLiterature(o: IPdfOptions): boolean {
	return o.activeOptions.includes("literature")
}

// iOS: WorkPDFPageType (discriminated union)
export type TPdfPageType =
	| { kind: "cover"; templateBytes: Uint8Array | null; layoutType: TPdfLayoutType }
	| { kind: "wcCover"; templateBytes: Uint8Array | null }
	| { kind: "wcImageCover"; templateBytes: Uint8Array | null }
	| { kind: "image"; fill: boolean }
	| { kind: "imageInfo"; item: IPdfPageData; templateBytes: Uint8Array | null; layoutType: TPdfLayoutType }
	| { kind: "wcImageInfo"; item: IPdfPageData; templateBytes: Uint8Array | null }
	| { kind: "attachment"; attachment: IOrgAttachment }

// iOS: WorkPDFPageModel — one selectable page variant within a row
export interface IPdfPage {
	id: string
	type: TPdfPageType
	isSelected: boolean
	imageUrl: IPdfImageUrl | null
}

// iOS: WorkPDFRowModel — one row on the customise screen
export interface IPdfRow {
	id: string
	rowType: "cover" | "artwork" | "attachment"
	pages: IPdfPage[]
	// user-editable text: cover title on the cover row, price override on artwork rows
	customInput: string
	artist?: string
	item?: IPdfPageData
}

export type { TPdfLayoutType }
